import React, { useState, useEffect, useRef } from 'react';
import './UserAdminPanel.css';
import { BACKEND_URL, mediaUrl, profilePicUrl } from '../../config';
import {
	Loader2,
	MoreVertical,
	ShieldAlert,
	ShieldCheck,
	UserCheck,
	UserMinus,
	X,
	MapPin,
	FileText,
	Heart,
	Users,
	Clock,
	Info,
	Map,
	Activity,
	Copy,
	Check,
	Trash2,
	RefreshCw,
	ChevronDown,
	Building2,
	EyeOff,
	Eye,
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { useUser } from '../../UserContext';
import { formatPhone, phoneDigits } from '../../utils/phone';
import DefaultPFP from '../../Media/Icons/DefaultPFP.webp';
import DateRangePicker from '../../compos/DateRangePicker/DateRangePicker';
import Dropdown from '../../compos/Analytics/Dropdown';
// Organisations are DB rows now (admin panel → Organisations tab), so the
// dropdown loads them instead of carrying a copy that would drift.
import { useOrganisations } from '../../utils/organisations';

const YoutubeLinkPreview = ({
	videoId,
	postTitle,
	videoLink,
}: {
	videoId: string;
	postTitle: string;
	videoLink: string;
}) => {
	const [metadata, setMetadata] = useState<{ title?: string; author_name?: string } | null>(null);

	useEffect(() => {
		if (!videoId) return;
		fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`)
			.then((res) => res.json())
			.then((data) => setMetadata(data))
			.catch(() => { });
	}, [videoId]);

	return (
		<div className='relative w-full h-full group cursor-pointer overflow-hidden'>
			<img
				src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
				alt={metadata?.title || postTitle}
				className='w-full h-full object-cover'
				onError={(e) => {
					(e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/0.jpg`;
				}}
			/>

			{/* Authentic Top Bar Overlay */}
			<div className='absolute top-0 left-0 right-0 p-4 pt-4 bg-gradient-to-b from-black/90 via-black/40 to-transparent pointer-events-none text-left'>
				<h4 className='text-white text-lg font-black truncate leading-tight mb-1 tracking-tight shadow-sm'>
					{metadata?.title || postTitle}
				</h4>
				{metadata?.author_name && (
					<p className='text-gray-200 text-sm font-bold truncate opacity-90 tracking-wide'>{metadata.author_name}</p>
				)}
			</div>

			{/* Official YouTube Play Button */}
			<div className='absolute inset-0 flex items-center justify-center pointer-events-none text-left'>
				<div className='w-[68px] h-[48px] drop-shadow-2xl'>
					<svg height='100%' version='1.1' viewBox='0 0 68 48' width='100%'>
						<path
							d='M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 c-2.93,.78-4.63,3.26-5.42,6.19C0.06,12.82,0,24,0,24s0.06,11.18,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79,-0.13,27.1,-1.55c2.93,-0.78,4.64,-3.26,5.42,-6.19C67.94,35.18,68,24,68,24S67.94,12.82,66.52,7.74z'
							fill='#FF0000'
						></path>
						<path d='M 45,24 27,14 27,34' fill='#fff'></path>
					</svg>
				</div>
			</div>

			{/* Styled "Watch on YouTube" Pill Button */}
			<button
				className='yt-watch-pill'
				onClick={(e) => {
					e.stopPropagation();
					window.open(videoLink, '_blank', 'noopener,noreferrer');
				}}
			>
				<span style={{ marginRight: '8px' }}>Watch on</span>
				<div className='yt-icon'>
					<svg viewBox='0 0 24 24' className='w-full h-full'>
						<path
							className='yt-play-bg'
							d='M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z'
						/>
					</svg>
				</div>
				<span className='yt-logo-text'>YouTube</span>
			</button>
		</div>
	);
};

const AdminPostPlaceholderSVG = () => (
	<div className="w-full h-full bg-gray-100 overflow-hidden relative flex items-center justify-center animate-shimmer-wave">
		<svg className="w-24 h-24 text-gray-300 z-10 drop-shadow-sm relative" fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</svg>
	</div>
);

const AdminPostMedia = ({ src, alt, format }: { src: string; alt: string; format: string }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [hasError, setHasError] = useState(false);

	if (hasError)
		return (
			<div className='w-full h-full flex items-center justify-center bg-gray-100'>
				<AdminPostPlaceholderSVG />
			</div>
		);

	return (
		<div className='relative w-full h-full flex items-center justify-center overflow-hidden bg-gray-100'>
			{!isLoaded && (
				<div className='absolute inset-0 flex items-center justify-center pointer-events-none z-20'>
					<AdminPostPlaceholderSVG />
				</div>
			)}
			{format === 'mp4' || format === 'video' ? (
				<video
					src={src}
					className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
					muted
					playsInline
					onLoadedData={() => setIsLoaded(true)}
					onError={() => setHasError(true)}
				/>
			) : (
				<img
					src={src}
					alt={alt}
					loading='lazy'
					className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
					onLoad={() => setIsLoaded(true)}
					onError={() => setHasError(true)}
				/>
			)}
		</div>
	);
};


const SortDropdown = ({
	value,
	onChange,
}: {
	value: 'name' | 'createdAt';
	onChange: (val: 'name' | 'createdAt') => void;
}) => {
	const options: { value: 'name' | 'createdAt'; label: string }[] = [
		{ value: 'name', label: 'Name' },
		{ value: 'createdAt', label: 'Date Joined' },
	];

	return (
		<Dropdown
			value={value}
			options={options}
			onChange={(val: string) => onChange(val as 'name' | 'createdAt')}
			ariaLabel='Sort by'
		/>
	);
};

const TemplateSelector = ({
	usr,
	isUpdating,
	updateTemplate,
}: {
	usr: any;
	isUpdating: boolean;
	updateTemplate: (usr: any, template: string) => void;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	return (
		<div className='relative' ref={ref}>
			<button
				onClick={(e) => {
					e.stopPropagation();
					setIsOpen(!isOpen);
				}}
				disabled={isUpdating}
				className='flex items-center justify-between gap-2 bg-white border border-amber-300 hover:bg-amber-50 rounded-lg px-3 py-1.5 transition-colors focus:outline-none disabled:opacity-50 cursor-pointer w-[120px]'
			>
				<span className='text-xs font-black text-amber-800 leading-none mt-0.5'>
					{usr.reportTemplate === 'caretaker' ? 'Caretaker' : usr.reportTemplate === 'custom' ? 'Custom' : 'Default'}
				</span>
				<ChevronDown className={`w-3.5 h-3.5 text-amber-800 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
			</button>
			{isOpen && (
				<div className='absolute right-0 top-full mt-1 w-[120px] bg-white border border-amber-200 rounded-xl shadow-lg z-[100] overflow-hidden animate-in fade-in zoom-in duration-100'>
					<div
						className={`px-3 py-2 text-xs font-bold cursor-pointer transition-colors text-amber-800 hover:bg-amber-100 text-left ${(!usr.reportTemplate || usr.reportTemplate === 'default') ? 'bg-amber-50/50' : ''}`}
						onClick={(e) => {
							e.stopPropagation();
							updateTemplate(usr, 'default');
							setIsOpen(false);
						}}
					>
						Default
					</div>
					<div
						className={`px-3 py-2 text-xs font-bold cursor-pointer transition-colors text-amber-800 hover:bg-amber-100 text-left border-t border-amber-50 ${usr.reportTemplate === 'caretaker' ? 'bg-amber-50/50' : ''}`}
						onClick={(e) => {
							e.stopPropagation();
							updateTemplate(usr, 'caretaker');
							setIsOpen(false);
						}}
					>
						Caretaker
					</div>
				</div>
			)}
		</div>
	);
};

const OrganisationSelector = ({
	usr,
	isUpdating,
	updateOrganisation,
	organisations,
}: {
	usr: any;
	isUpdating: boolean;
	updateOrganisation: (usr: any, organisation: string) => void;
	organisations: { key: string }[];
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const current = usr.organisation || 'none';

	return (
		<div className='relative' ref={ref}>
			<button
				onClick={(e) => {
					e.stopPropagation();
					setIsOpen(!isOpen);
				}}
				disabled={isUpdating}
				className='flex items-center justify-between gap-2 bg-white border border-sky-300 hover:bg-sky-50 rounded-lg px-3 py-1.5 transition-colors focus:outline-none disabled:opacity-50 cursor-pointer w-[120px]'
			>
				<span className='text-xs font-black text-sky-800 leading-none mt-0.5 truncate'>
					{usr.organisation || 'None'}
				</span>
				<ChevronDown className={`w-3.5 h-3.5 text-sky-800 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
			</button>
			{isOpen && (
				<div className='absolute right-0 top-full mt-1 w-[120px] bg-white border border-sky-200 rounded-xl shadow-lg z-[100] overflow-hidden animate-in fade-in zoom-in duration-100'>
					<div
						className={`px-3 py-2 text-xs font-bold cursor-pointer transition-colors text-sky-800 hover:bg-sky-100 text-left ${current === 'none' ? 'bg-sky-50/50' : ''}`}
						onClick={(e) => {
							e.stopPropagation();
							updateOrganisation(usr, 'none');
							setIsOpen(false);
						}}
					>
						None
					</div>
					{organisations.map((org) => (
						<div
							key={org.key}
							className={`px-3 py-2 text-xs font-bold cursor-pointer transition-colors text-sky-800 hover:bg-sky-100 text-left border-t border-sky-50 ${current === org.key ? 'bg-sky-50/50' : ''}`}
							onClick={(e) => {
								e.stopPropagation();
								updateOrganisation(usr, org.key);
								setIsOpen(false);
							}}
						>
							{org.key}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const UserAdminPanel = () => {
	const navigate = useNavigate();
	const { user: currentUser } = useUser();
	// Live list — an organisation added in the Organisations tab shows up here
	// without a redeploy.
	const organisations = useOrganisations();

	const [phoneNumber, setPhoneNumber] = useState<string>('');
	const [allUsersList, setAllUsersList] = useState<any[]>([]);
	const [usersList, setUsersList] = useState<any[]>([]);
	const [responseMessage, setResponseMessage] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [sortField, setSortField] = useState<'name' | 'createdAt'>('name');
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
	const [fromDate, setFromDate] = useState<string>('');
	const [toDate, setToDate] = useState<string>('');

	// Track which dropdown is open (by user id)
	const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
	const [copiedId, setCopiedId] = useState<string | null>(null);

	const handleCopy = (text: string) => {
		navigator.clipboard.writeText(text);
		setCopiedId(text);
		setTimeout(() => setCopiedId(null), 2000);
	};
	// Track which users have an in-flight request (by user id or "id:action" string)
	const [updatingUsers, setUpdatingUsers] = useState<Set<string | number>>(new Set());

	// Detailed view state
	const [selectedUser, setSelectedUser] = useState<any | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [confirmPhoneInput, setConfirmPhoneInput] = useState('');

	const [modalView, setModalView] = useState<'info' | 'posts'>('info');
	const [userPosts, setUserPosts] = useState<any[]>([]);
	const [isLoadingPosts, setIsLoadingPosts] = useState(false);
	const [isDeletingPost, setIsDeletingPost] = useState<number | null>(null);
	const [confirmPostInput, setConfirmPostInput] = useState<string>('');

	// Anonymous posts are covered by default in the posts view; each one must be
	// revealed by typing "showanonymous". The revealed set lives only for the
	// current user-modal visit — closeModal() wipes it, so reopening the modal
	// hides anonymous posts again. (Display-only friction; the data is already
	// in the admin's browser.)
	const [revealedPostIds, setRevealedPostIds] = useState<number[]>([]);
	const [revealingPostId, setRevealingPostId] = useState<number | null>(null);
	const [confirmRevealInput, setConfirmRevealInput] = useState<string>('');

	// Toast
	const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
	const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const showToast = (message: string, type: 'success' | 'error' = 'success') => {
		if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
		setToast({ message, type });
		toastTimerRef.current = setTimeout(() => setToast(null), 3500);
	};

	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
				setActiveDropdown(null);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Restore component state returning from a nested view like single post
	useEffect(() => {
		const saved = sessionStorage.getItem('userAdminState');
		let restoredList = false;
		if (saved) {
			try {
				const state = JSON.parse(saved);
				if (state.phoneNumber) setPhoneNumber(state.phoneNumber);
				if (state.allUsersList) {
					setAllUsersList(state.allUsersList);
					restoredList = true;
				}
				if (state.usersList) setUsersList(state.usersList);
				if (state.responseMessage) setResponseMessage(state.responseMessage);
				if (state.selectedUser) {
					setSelectedUser(state.selectedUser);
					setIsModalOpen(true);
					setModalView(state.modalView || 'info');
					setUserPosts(state.userPosts || []);
					// Same modal visit (came back from an opened post) — keep reveals.
					setRevealedPostIds(state.revealedPostIds || []);

					if (state.scrollPos) {
						setTimeout(() => {
							const scrollDiv = document.getElementById('admin-modal-scroll');
							if (scrollDiv) scrollDiv.scrollTop = state.scrollPos;
						}, 100);
					}
				}
			} catch (e) {
				console.error('Failed to restore admin state', e);
			}
			sessionStorage.removeItem('userAdminState');
		}
		if (!restoredList) {
			fetchAllUsers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchAllUsers = async () => {
		setIsLoading(true);
		setResponseMessage('Loading users...');
		try {
			const response = await fetch(`${BACKEND_URL}/users/listAll?sortOrder=desc`, {
				method: 'GET',
				credentials: 'include',
				headers: getAuthHeaders(),
			});
			if (response.ok) {
				const data = await response.json();
				if (Array.isArray(data)) {
					setAllUsersList(data);
				}
			} else {
				setResponseMessage('Error fetching users.');
			}
		} catch (error) {
			console.error(error);
			setResponseMessage('Network error fetching users.');
		} finally {
			setIsLoading(false);
		}
	};
	// Handle Escape key & disable background scroll when modal is open
	useEffect(() => {
		const handleEsc = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closeModal();
		};
		if (isModalOpen) {
			window.addEventListener('keydown', handleEsc);
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			window.removeEventListener('keydown', handleEsc);
			document.body.style.overflow = '';
		};
	}, [isModalOpen]);

	const getAuthHeaders = () => {
		return {
		};
	};

	const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		if (/^[a-zA-Z0-9\s]*$/.test(val)) {
			setPhoneNumber(val);
		}
	};

	// Frontend filtering
	useEffect(() => {
		let filtered = [...allUsersList];

		if (fromDate) {
			const f = new Date(fromDate).getTime();
			filtered = filtered.filter((u) => new Date(u.createdAt).getTime() >= f);
		}
		if (toDate) {
			const t = new Date(toDate);
			t.setHours(23, 59, 59, 999);
			filtered = filtered.filter((u) => new Date(u.createdAt).getTime() <= t.getTime());
		}

		const trimmedValue = phoneNumber.trim().toLowerCase();
		if (trimmedValue) {
			filtered = filtered.filter(
				(u) =>
					(u.name && u.name.toLowerCase().includes(trimmedValue)) ||
					(u.phoneNumber && u.phoneNumber.toString().includes(trimmedValue))
			);
		}

		filtered.sort((a, b) => {
			if (sortField === 'createdAt') {
				const vA = new Date(a.createdAt || 0).getTime();
				const vB = new Date(b.createdAt || 0).getTime();
				return sortOrder === 'asc' ? vB - vA : vA - vB;
			} else {
				const nameA = String(a.name || '').toLowerCase();
				const nameB = String(b.name || '').toLowerCase();
				return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
			}
		});

		setUsersList(filtered);

		if (allUsersList.length === 0) {
			// Initial state handled by fetchAllUsers message
		} else if (filtered.length > 0) {
			setResponseMessage(`Showing ${filtered.length} user(s).`);
		} else {
			setResponseMessage('No users found matching your filters.');
		}
	}, [phoneNumber, fromDate, toDate, sortOrder, sortField, allUsersList]);

	// ---- Action handlers ----

	const setUserUpdating = (userId: number, updating: boolean, action?: string) => {
		const key = action ? `${userId}:${action}` : userId;
		setUpdatingUsers((prev) => {
			const next = new Set(prev);
			updating ? next.add(key) : next.delete(key);
			return next;
		});
	};

	const isAnyUpdating = (userId: number) => {
		return Array.from(updatingUsers).some(
			(key) => key === userId || (typeof key === 'string' && key.startsWith(`${userId}:`))
		);
	};

	const updateUserInList = (userId: number, patch: Partial<any>) => {
		setAllUsersList((prev) => prev.map((u) => (u.id === userId ? { ...u, ...patch } : u)));
		setSelectedUser((prev: any) => (prev && prev.id === userId ? { ...prev, ...patch } : prev));
	};

	const toggleBlockStatus = async (usr: any) => {
		setActiveDropdown(null);
		// Prevent admins from blocking their own account
		if (!usr.isBlocked && currentUser && String(currentUser.phoneNumber) === String(usr.phoneNumber)) {
			showToast('You cannot block your own account.', 'error');
			return;
		}
		setUserUpdating(usr.id, true, 'block');
		const action = usr.isBlocked ? 'unblock' : 'block';
		try {
			const response = await fetch(`${BACKEND_URL}/users/${usr.phoneNumber}/${action}`, {
				method: 'PUT',
				credentials: 'include',
				headers: getAuthHeaders(),
			});
			if (response.ok) {
				updateUserInList(usr.id, { isBlocked: !usr.isBlocked });
			} else {
				console.error(`Failed to ${action} user`);
			}
		} catch (err) {
			console.error(err);
		} finally {
			setUserUpdating(usr.id, false, 'block');
		}
	};

	const toggleAuthorityStatus = async (usr: any) => {
		setActiveDropdown(null);
		setUserUpdating(usr.id, true, 'authority');
		try {
			if (usr.is_authority) {
				// Demote — use new phone-based endpoint
				const response = await fetch(`${BACKEND_URL}/users/authority/phone/${usr.phoneNumber}`, {
					method: 'DELETE',
					credentials: 'include',
					headers: getAuthHeaders(),
				});
				if (response.ok) {
					updateUserInList(usr.id, { is_authority: false });
				} else {
					console.error('Failed to demote authority');
				}
			} else {
				// Promote — use existing createAuthority endpoint
				const response = await fetch(`${BACKEND_URL}/users/authority/createAuthority`, {
					method: 'POST',
					credentials: 'include',
					headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
					body: JSON.stringify({ name: usr.name, phoneNumber: String(usr.phoneNumber) }),
				});
				if (response.ok) {
					updateUserInList(usr.id, { is_authority: true });
				} else {
					console.error('Failed to promote authority');
				}
			}
		} catch (err) {
			console.error(err);
		} finally {
			setUserUpdating(usr.id, false, 'authority');
		}
	};

	const updateTemplate = async (usr: any, template: string) => {
		if (!usr || template === 'custom') return;
		if ((usr.reportTemplate || 'default') === template) return;
		setUserUpdating(usr.id, true, 'template');
		try {
			const response = await fetch(`${BACKEND_URL}/users/authority/${usr.phoneNumber}/template`, {
				method: 'PUT',
				credentials: 'include',
				headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
				body: JSON.stringify({ template }),
			});
			if (response.ok) {
				const data = await response.json().catch(() => ({}));
				updateUserInList(usr.id, {
					reportTemplate: data.template ?? template,
					requiredFields: data.requiredFields ?? [],
				});
			} else {
				console.error('Failed to update template');
			}
		} catch (err) {
			console.error(err);
		} finally {
			setUserUpdating(usr.id, false, 'template');
		}
	};

	const updateOrganisation = async (usr: any, organisation: string) => {
		if (!usr) return;
		if ((usr.organisation || 'none') === organisation) return;
		setUserUpdating(usr.id, true, 'organisation');
		try {
			const response = await fetch(`${BACKEND_URL}/users/authority/${usr.phoneNumber}/organisation`, {
				method: 'PUT',
				credentials: 'include',
				headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
				body: JSON.stringify({ organisation }),
			});
			if (response.ok) {
				const data = await response.json().catch(() => ({}));
				updateUserInList(usr.id, {
					organisation: data.organisation ?? (organisation === 'none' ? null : organisation),
				});
			} else {
				console.error('Failed to update organisation');
			}
		} catch (err) {
			console.error(err);
		} finally {
			setUserUpdating(usr.id, false, 'organisation');
		}
	};

	const handleUserClick = async (usr: any) => {
		// The list now carries only light fields (name, phone, joined, blocked,
		// authority, template) so the whole directory can load at once. The modal
		// needs the rest (email, gender, dob, counts), so fetch the full record on
		// click and merge it in. Show the modal immediately with what we have.
		setSelectedUser(usr);
		setIsModalOpen(true);
		try {
			const response = await fetch(`${BACKEND_URL}/users/${usr.phoneNumber}`, {
				method: 'GET',
				credentials: 'include',
				headers: getAuthHeaders(),
			});
			if (response.ok) {
				const full = await response.json();
				// Only apply if the user hasn't clicked away to a different card.
				setSelectedUser((prev: any) =>
					prev && prev.phoneNumber === usr.phoneNumber ? { ...prev, ...full } : prev
				);
			}
		} catch (error) {
			console.error('Failed to load user detail:', error);
		}
	};

	// An admin deleting their own account is irreversible and locks them out of
	// this panel for good. The backend refuses it outright (ApiService
	// .deleteUserByAdmin); this is the matching UI guard so the button is never
	// live in the first place.
	const isSelfAccount = (usr: any) => {
		if (!currentUser || !usr) return false;
		if (usr.id != null && currentUser.id != null) {
			return String(currentUser.id) === String(usr.id);
		}
		return String(currentUser.phoneNumber) === String(usr.phoneNumber);
	};

	const handleDeleteUser = async () => {
		if (!selectedUser || confirmPhoneInput !== 'deleteuser') return;
		if (isSelfAccount(selectedUser)) {
			showToast('You cannot delete your own admin account.', 'error');
			setIsDeleting(false);
			setConfirmPhoneInput('');
			return;
		}

		setUserUpdating(selectedUser.id, true, 'delete');
		try {
			const response = await fetch(`${BACKEND_URL}/users/delete/${selectedUser.phoneNumber}`, {
				method: 'DELETE',
				credentials: 'include',
				headers: getAuthHeaders(),
			});

			if (response.ok) {
				closeModal();
				showToast(`"${selectedUser.name}" permanently deleted.`);
				// Re-run the search to refresh the list + count
				fetchAllUsers();
			} else {
				const errData = await response.json().catch(() => ({}));
				showToast(errData.message || 'Failed to delete user. Please try again.', 'error');
				console.error('Failed to delete user', errData);
			}
		} catch (err) {
			console.error(err);
		} finally {
			setUserUpdating(selectedUser.id, false, 'delete');
			setIsDeleting(false);
			setConfirmPhoneInput('');
		}
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setSelectedUser(null);
		setIsDeleting(false);
		setConfirmPhoneInput('');
		setModalView('info');
		setUserPosts([]);
		setIsDeletingPost(null);
		setConfirmPostInput('');
		setRevealedPostIds([]);
		setRevealingPostId(null);
		setConfirmRevealInput('');
	};

	const fetchUserPosts = async () => {
		if (!selectedUser) return;
		setIsLoadingPosts(true);
		try {
			const response = await fetch(`${BACKEND_URL}/users/posts/${selectedUser.phoneNumber}`, {
				method: 'GET',
				credentials: 'include',
				headers: getAuthHeaders(),
			});
			if (response.ok) {
				const data = await response.json();
				setUserPosts(data);
			} else {
				showToast('Failed to fetch user posts', 'error');
			}
		} catch (err) {
			console.error(err);
			showToast('Error connecting to server', 'error');
		} finally {
			setIsLoadingPosts(false);
		}
	};

	const handleDeletePost = async (postId: number) => {
		if (confirmPostInput !== 'deletepost') return;

		try {
			const response = await fetch(`${BACKEND_URL}/users/post/${postId}`, {
				method: 'DELETE',
				credentials: 'include',
				headers: getAuthHeaders(),
			});
			if (response.ok) {
				showToast('Post permanently deleted.');
				setUserPosts((prev) => prev.filter((p) => p.id !== postId));
				setSelectedUser((prev: any) => (prev ? { ...prev, postCount: Math.max(0, prev.postCount - 1) } : null));
			} else {
				const errData = await response.json().catch(() => ({}));
				showToast(errData.message || 'Failed to delete post.', 'error');
			}
		} catch (err) {
			console.error(err);
			showToast('Network error while deleting post', 'error');
		} finally {
			setIsDeletingPost(null);
			setConfirmPostInput('');
		}
	};

	const handlePostClick = (postId: number) => {
		const scrollDiv = document.getElementById('admin-modal-scroll');
		const scrollPos = scrollDiv ? scrollDiv.scrollTop : 0;

		sessionStorage.setItem(
			'userAdminState',
			JSON.stringify({
				phoneNumber,
				allUsersList,
				usersList,
				responseMessage,
				selectedUser,
				modalView,
				userPosts,
				revealedPostIds,
				scrollPos,
			})
		);
		navigate(`/getpost/${postId}`);
	};

	const extractYoutubeId = (url: string) => {
		const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
		const match = url.match(regExp);
		return match && match[2].length === 11 ? match[2] : null;
	};

	const formatDate = (date: any) => {
		if (!date) return 'N/A';
		return new Date(date).toLocaleDateString('en-IN', {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		});
	};

	const formatJoinedDate = (date: any) => {
		if (!date) return '';
		const d = new Date(date);
		if (isNaN(d.getTime())) return '';
		const day = String(d.getDate()).padStart(2, '0');
		const month = d.toLocaleString('en-US', { month: 'short' });
		const year = d.getFullYear();
		return `${day} ${month} ${year}`;
	};

	// ---- Avatar Component ----

	const UserAvatar = ({
		pfpURL,
		name,
		size = 'large',
	}: {
		pfpURL?: string;
		name: string;
		size?: 'small' | 'large';
	}) => {
		const [imgSrc, setImgSrc] = useState<string>(DefaultPFP);
		const [hasError, setHasError] = useState(false);

		useEffect(() => {
			if (pfpURL && pfpURL !== 'null' && !hasError) {
				setImgSrc(profilePicUrl(pfpURL));
			} else {
				setImgSrc(DefaultPFP);
			}
		}, [pfpURL, hasError]);

		const containerSize = size === 'large' ? 'w-14 h-14' : 'w-10 h-10';

		return (
			<div
				className={`${containerSize} bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/30 overflow-hidden shrink-0`}
			>
				<img
					src={imgSrc}
					alt={name}
					className='w-full h-full object-cover'
					onError={() => {
						if (!hasError) {
							setHasError(true);
							setImgSrc(DefaultPFP);
						}
					}}
				/>
			</div>
		);
	};

	// ---- Highlighting ----

	// Phone numbers are stored as raw E.164 digits (919876543210). Show them
	// grouped instead. Substring highlighting can't survive the inserted spaces,
	// so match on digits and highlight the number as a whole — that still says
	// "this row matched on the phone" without rendering an unreadable blob.
	const renderHighlightedPhone = (raw: any, query: string) => {
		const pretty = formatPhone(raw);
		const q = phoneDigits(query);
		const matched = q.length > 0 && phoneDigits(raw).includes(q);
		return matched ? (
			<span className='bg-blue-200 text-blue-800 rounded px-0.5 font-bold'>{pretty}</span>
		) : (
			<span>{pretty}</span>
		);
	};

	const renderHighlightedText = (text: any, query: string) => {
		const safeText = text ? String(text) : '';
		const trimmedQuery = query.trim();
		if (!trimmedQuery) return <span>{safeText}</span>;
		try {
			const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const regex = new RegExp(`(${escapedQuery})`, 'gi');
			const parts = safeText.split(regex);
			return (
				<>
					{parts.map((part, i) =>
						part.toLowerCase() === trimmedQuery.toLowerCase() ? (
							<span key={i} className='bg-blue-200 text-blue-800 rounded px-0.5 font-bold'>
								{part}
							</span>
						) : (
							<span key={i}>{part}</span>
						)
					)}
				</>
			);
		} catch (e) {
			return <span>{safeText}</span>;
		}
	};

	// ---- Render ----

	return (
		<div className='bg-gray-50 flex-1 min-h-full font-Montserrat'>
			{/* Toast Notification */}
			{toast && (
				<div
					className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border transition-all duration-300 ${
						toast.type === 'error' ? 'bg-red-600 border-red-500 text-white' : 'bg-gray-900 border-gray-700 text-white'
					}`}
				>
					{toast.type === 'error' ? (
						<X className='w-4 h-4 shrink-0' />
					) : (
						<Trash2 className='w-4 h-4 shrink-0 text-red-400' />
					)}
					<span className='text-sm font-semibold'>{toast.message}</span>
				</div>
			)}
			<div className='max-w-6xl mx-auto px-4 pb-5'>
				<h1 className='text-3xl font-extrabold text-blue-900 mb-8'>Manage Users</h1>

				{/* Search & Filter Bar */}
				<div className='bg-white p-2 rounded-2xl shadow-sm border border-gray-100 flex items-center mb-6'>
					<div className='flex-1 flex gap-2 items-center'>
						<div className='relative flex-1'>
							<input
								type='text'
								className='w-full pl-4 pr-3 py-1 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-gray-700 font-semibold h-[34px] text-sm'
								placeholder='Search by user name or phone number...'
								value={phoneNumber}
								onChange={handleSearchInput}
								onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
							/>
						</div>

						<DateRangePicker
							initialStartDate={fromDate}
							initialEndDate={toDate}
							onRangeChange={(start, end) => {
								setFromDate(start);
								setToDate(end);
							}}
							iconOnly={true}
						/>

						{/* Divider before sorting */}
						<div className='w-px h-6 bg-gray-200 mx-1'></div>

						<div className='flex items-center gap-2'>
							<span className='text-sm font-bold text-gray-500 ml-1 hidden sm:inline'>Sort by:</span>
							<SortDropdown value={sortField} onChange={setSortField} />
							<button
								onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
								className={`px-3 h-[34px] w-10 border border-gray-200 rounded-xl transition-colors flex items-center justify-center outline-none focus:outline-none focus:ring-0 bg-gray-50 hover:bg-blue-50/50 cursor-pointer text-gray-700`}
								title={sortField === 'createdAt' ? (sortOrder === 'asc' ? 'Newest First' : 'Oldest First') : (sortOrder === 'asc' ? 'Sort A-Z' : 'Sort Z-A')}
							>
								<span className='text-sm leading-none font-bold'>{sortOrder === 'asc' ? '↑' : '↓'}</span>
							</button>
						</div>

						{/* Divider before refresh */}
						<div className='w-px h-6 bg-gray-200 mx-1 ml-auto sm:ml-1'></div>

						<button
							className='bg-blue-600 hover:bg-blue-700 text-white px-3 rounded-xl font-bold transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2 h-[34px] text-sm w-fit sm:w-[100px]'
							onClick={fetchAllUsers}
							disabled={isLoading}
						>
							{isLoading ? <Loader2 className='w-4 h-4 animate-spin' /> : <><RefreshCw className='w-4 h-4' /> <span className='hidden sm:inline'>Refresh</span></>}
						</button>
					</div>
				</div>

				{/* Status Message */}
				{responseMessage && (
					<div
						className={`mt-2 py-1.5 px-4 flex items-center justify-center rounded-xl ${usersList.length === 0 ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-blue-50 text-blue-600 border border-blue-100'}`}
					>
						<p className='font-semibold text-center m-0 leading-none'>{responseMessage}</p>
					</div>
				)}

				{/* Results */}
				<div className='mt-4' style={{ paddingBottom: activeDropdown !== null ? '240px' : '0px' }}>
					{isLoading ? (
						<div className='flex flex-col items-center justify-center py-20 opacity-50'>
							<Loader2 className='w-12 h-12 animate-spin text-blue-600 mb-4' />
							<p className='text-gray-500 font-medium'>Hunting down users...</p>
						</div>
					) : (
						<div className='w-full'>
							{(() => {
								const renderUserCard = (usr: any) => {
									const isUpdating = isAnyUpdating(usr.id);
									const isOpen = activeDropdown === usr.id;
									return (
										<div
											key={usr.id}
											className='bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer flex flex-col gap-1'
											onClick={() => handleUserClick(usr)}
										>
											<div className='flex justify-between items-center text-left'>
												<div className='text-left flex-1 min-w-0'>
													<h3 className='text-lg font-bold text-gray-900 truncate m-0' title={typeof usr.name === 'string' ? usr.name : ''}>
														{renderHighlightedText(usr.name, phoneNumber)}
													</h3>
													<p className='text-gray-500 font-medium text-sm m-0 truncate'>
														{renderHighlightedPhone(usr.phoneNumber, phoneNumber)}
														{usr.createdAt && (
															<>
																<span className='mx-1.5 text-gray-400'>•</span>
																<span className='font-normal text-gray-400 text-[12px]'>{formatJoinedDate(usr.createdAt)}</span>
															</>
														)}
													</p>
												</div>

												<div className='flex items-center justify-end gap-2 ml-2 -mr-1 shrink-0 min-w-[175px]' ref={isOpen ? dropdownRef : null}>
													{(usr.isBlocked || usr.is_authority) && (
														<div className='grid grid-cols-[max-content_max-content] grid-rows-2 gap-x-1.5 gap-y-1 justify-end justify-items-stretch'>
															{usr.is_authority && usr.organisation && (
																<span className='col-start-1 row-start-1 bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide w-full flex justify-center whitespace-nowrap'>
																	{usr.organisation}
																</span>
															)}
															{usr.isBlocked && (
																<span className='col-start-1 row-start-2 bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide w-full flex justify-center whitespace-nowrap'>
																	Blocked
																</span>
															)}
															{usr.is_authority && (
																<span className='col-start-2 row-start-1 bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide w-full flex justify-center whitespace-nowrap'>
																	Authority
																</span>
															)}
															{usr.is_authority && usr.reportTemplate && usr.reportTemplate !== 'default' && (
																<span className='col-start-2 row-start-2 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide w-full flex justify-center whitespace-nowrap'>
																	{usr.reportTemplate === 'caretaker' ? 'Caretaker' : 'Custom'}
																</span>
															)}
														</div>
													)}

													{/* 3-dot button */}
													<div className='relative'>
														<button
															className={`h-[44px] px-2 bg-gray-50 hover:bg-gray-200 rounded-[9px] transition-colors flex items-center justify-center focus:outline-none ${isUpdating ? 'opacity-40 cursor-not-allowed' : 'text-gray-500 hover:text-gray-900'}`}
															onClick={(e) => {
																e.stopPropagation();
																if (!isUpdating) setActiveDropdown(isOpen ? null : usr.id);
															}}
															disabled={isUpdating}
														>
															{isUpdating ? (
																<Loader2 className='w-5 h-5 animate-spin' />
															) : (
																<MoreVertical className='w-5 h-5' />
															)}
														</button>

														{/* Dropdown menu */}
														{isOpen && (
															<div
																className='absolute right-0 top-full mt-1 w-max min-w-[220px] bg-white border border-gray-200 rounded-xl shadow-lg z-50'
																onClick={(e) => e.stopPropagation()}
															>
																{/* Block / Unblock */}
																{(() => {
																	const isSelf = !usr.isBlocked && currentUser && String(currentUser.phoneNumber) === String(usr.phoneNumber);
																	return (
																		<button
																			className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold transition-colors text-left focus:outline-none whitespace-nowrap rounded-t-xl
                                    ${isSelf ? 'text-gray-400 cursor-not-allowed' : 'text-red-700 hover:bg-red-50'}`}
																			onClick={() => !isSelf && toggleBlockStatus(usr)}
																			disabled={!!isSelf}
																			title={isSelf ? 'You cannot block your own account' : undefined}
																		>
																			{usr.isBlocked ? (
																				<>
																					<ShieldCheck className='w-4 h-4 shrink-0' /> Unblock User
																				</>
																			) : (
																				<>
																					<ShieldAlert className='w-4 h-4 shrink-0' /> {isSelf ? 'Block User (You)' : 'Block User'}
																				</>
																			)}
																		</button>
																	);
																})()}

																<div className='border-t border-gray-100 mx-3' />

																{/* Promote / Demote */}
																<button
																	className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold transition-colors text-left focus:outline-none whitespace-nowrap ${usr.is_authority ? '' : 'rounded-b-xl'}
                                    text-indigo-700 hover:bg-indigo-50`}
																	onClick={() => toggleAuthorityStatus(usr)}
																>
																	{usr.is_authority ? (
																		<>
																			<UserMinus className='w-4 h-4 shrink-0' /> Demote Authority
																		</>
																	) : (
																		<>
																			<UserCheck className='w-4 h-4 shrink-0' /> Promote to Authority
																		</>
																	)}
																</button>

																{/* Template Selection */}
																{usr.is_authority && (
																	<>
																		<div className='border-t border-gray-100 mx-3' />
																		<div className='w-full flex items-center justify-between gap-4 px-4 py-2 text-sm font-semibold transition-colors text-amber-700 hover:bg-amber-50'>
																			<span className='flex items-center gap-3 whitespace-nowrap'>
																				{updatingUsers.has(`${usr.id}:template`) ? (
																					<Loader2 className='w-4 h-4 shrink-0 animate-spin' />
																				) : (
																					<FileText className='w-4 h-4 shrink-0' />
																				)}
																				Template
																			</span>
																			<div className='flex items-center gap-2'>
																				<TemplateSelector
																					usr={usr}
																					isUpdating={isAnyUpdating(usr.id)}
																					updateTemplate={updateTemplate}
																				/>
																			</div>
																		</div>
																	</>
																)}

																{/* Organisation Selection */}
																{usr.is_authority && (
																	<>
																		<div className='border-t border-gray-100 mx-3' />
																		<div className='w-full flex items-center justify-between gap-4 px-4 py-2 text-sm font-semibold transition-colors text-sky-700 hover:bg-sky-50 rounded-b-xl'>
																			<span className='flex items-center gap-3 whitespace-nowrap'>
																				{updatingUsers.has(`${usr.id}:organisation`) ? (
																					<Loader2 className='w-4 h-4 shrink-0 animate-spin' />
																				) : (
																					<Building2 className='w-4 h-4 shrink-0' />
																				)}
																				Organisation
																			</span>
																			<div className='flex items-center gap-2'>
																				<OrganisationSelector
																					usr={usr}
																					isUpdating={isAnyUpdating(usr.id)}
																					updateOrganisation={updateOrganisation}
																					organisations={organisations}
																				/>
																			</div>
																		</div>
																	</>
																)}
															</div>
														)}
													</div>
												</div>
											</div>
										</div>
									);
								};

								let displayUsers = [...usersList];
								let isGrouped = sortField === 'createdAt';

								if (isGrouped) {
									const grouped: Record<string, any[]> = {};
									displayUsers.forEach((usr: any) => {
										const date = new Date(usr.createdAt);
										const monthKey = date.toLocaleString('default', { month: 'long', year: 'numeric' });
										if (!grouped[monthKey]) grouped[monthKey] = [];
										grouped[monthKey].push(usr);
									});

									return (
										<div className='flex flex-col gap-6 w-full'>
											{Object.keys(grouped).map((monthKey, index) => (
												<div
													key={monthKey}
													className={`flex flex-col gap-1.5 ${index !== 0 ? 'border-t border-gray-100 pt-6 mt-2' : ''}`}
												>
													<h3 className='text-xs font-bold uppercase tracking-[0.15em] text-gray-400 flex items-center gap-2'>
														<span>{monthKey}</span>
														<span className='w-1.5 h-1.5 rounded-full bg-gray-200' />
														<span className='font-medium lowercase tracking-normal text-gray-300'>
															{grouped[monthKey].length} users
														</span>
													</h3>
													<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
														{grouped[monthKey].map((usr: any) => renderUserCard(usr))}
													</div>
												</div>
											))}
										</div>
									);
								}

								return (
									<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
										{displayUsers.map((usr: any) => renderUserCard(usr))}
									</div>
								);
							})()}
						</div>
					)}
				</div>
			</div>

			{/* User Detail Modal */}
			{isModalOpen && selectedUser && (
				<div className='fixed inset-0 z-[1100] flex items-center justify-center p-4'>
					{/* Backdrop */}
					<div className='absolute inset-0 bg-black/40 transition-opacity' onClick={closeModal}></div>

					{/* Modal Content */}
					<div className='relative bg-white w-full max-w-2xl max-h-[90vh] rounded-2xl border border-gray-200 overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200'>
						{/* Header: Pure Identification */}
						<div className='bg-white border-b border-gray-100 p-3 shrink-0'>
							<div className='flex justify-between items-center'>
								<div className='flex gap-3 items-center'>
									<UserAvatar pfpURL={selectedUser.profilepicURL} name={selectedUser.name} size='large' />
									<div className='flex flex-col items-start'>
										<h2 className='text-xl font-bold text-gray-900 leading-none m-0'>{selectedUser.name}</h2>
										<p className='text-gray-500 text-sm font-medium m-0 mt-1'>{formatPhone(selectedUser.phoneNumber)}</p>
									</div>
								</div>
								<button
									onClick={closeModal}
									className='p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-900 rounded-lg transition-colors border border-transparent hover:border-gray-200'
								>
									<X className='w-5 h-5' />
								</button>
							</div>
						</div>

						{/* Body: Functional Layout */}
						<div className='overflow-y-auto px-5 pb-5 pt-0 bg-white' id='admin-modal-scroll'>
							{modalView === 'info' ? (
								<div className='flex flex-col gap-4 pt-3'>
									{/* Stats Bar: Balanced and Compact */}
									<div className='grid grid-cols-2 sm:grid-cols-4 gap-2'>
										{[
											{
												label: 'Posts',
												value: selectedUser.postCount,
												icon: FileText,
												color: 'text-blue-600',
												bg: 'bg-blue-50',
											},
											{
												label: 'Reports',
												value: selectedUser.reportCount,
												icon: Activity,
												color: 'text-orange-600',
												bg: 'bg-orange-50',
											},
											{
												label: 'Likes',
												value: selectedUser.likeCount,
												icon: Heart,
												color: 'text-red-600',
												bg: 'bg-red-50',
											},
											{
												label: 'Followers',
												value: selectedUser.followerCount,
												icon: Users,
												color: 'text-indigo-600',
												bg: 'bg-indigo-50',
											},
										].map((stat, i) => (
											<div
												key={i}
												className='border border-gray-100 rounded-xl p-3 flex flex-col items-center justify-center text-center'
											>
												<stat.icon className={`w-4 h-4 ${stat.color} mb-1`} />
												<span className='text-lg font-bold text-gray-900'>{stat.value || 0}</span>
												<span className='text-[9px] uppercase tracking-wider font-bold text-gray-400'>
													{stat.label}
												</span>
											</div>
										))}
									</div>

									{/* Info Sections: Bordered Boxes */}
									<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
										{/* Basic Info */}
										<div className='border border-gray-100 rounded-xl pt-4 px-4 pb-2 space-y-4'>
											<h3 className='text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2'>
												<Info className='w-3.5 h-3.5' /> Basic Information
											</h3>
											<div className='space-y-1'>
												<div>
													<p className='text-[9px] font-bold text-gray-400 uppercase leading-none mb-1 text-left'>
														Email Address
													</p>
													<div className='flex items-center gap-1 group min-h-[24px] w-full min-w-0'>
														<p
															className={`text-sm font-semibold text-gray-800 m-0 flex-1 whitespace-nowrap overflow-x-auto no-scrollbar text-left ${selectedUser.email && selectedUser.email.length > 20
																	? 'tracking-tighter'
																	: ''
																}`}
														>
															{selectedUser.email || 'No email'}
														</p>
														{selectedUser.email && (
															<button
																onClick={() => handleCopy(selectedUser.email)}
																className='inline-flex items-center justify-center p-1 hover:bg-gray-100 rounded transition-colors text-gray-400 hover:text-blue-600 focus:outline-none shrink-0'
																title='Copy Email'
															>
																{copiedId === selectedUser.email ? (
																	<Check size={12} className='text-green-600' />
																) : (
																	<Copy size={12} />
																)}
															</button>
														)}
													</div>
												</div>
												<div className='flex border-t border-gray-50 pt-1.5'>
													<div className='w-1/2 text-left'>
														<p className='text-[9px] font-bold text-gray-400 uppercase leading-none mb-1'>Gender</p>
														<p className='text-sm font-semibold text-gray-800'>
															{selectedUser.gender === 'M' ? 'Male' : selectedUser.gender === 'F' ? 'Female' : 'Other'}
														</p>
													</div>
													<div className='w-1/2 text-left pl-2'>
														<p className='text-[9px] font-bold text-gray-400 uppercase leading-none mb-1'>
															Date of Birth
														</p>
														<p className='text-sm font-semibold text-gray-800'>{formatDate(selectedUser.dob)}</p>
													</div>
												</div>
											</div>
										</div>

										{/* Status & Permissions */}
										<div className='border border-gray-100 rounded-xl pt-4 px-4 pb-2 space-y-4'>
											<h3 className='text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2'>
												<ShieldCheck className='w-3.5 h-3.5' /> Status & Permissions
											</h3>
											<div className='grid grid-cols-1 gap-2'>
												{/* Block Status Box */}
												<div className='relative p-2.5 rounded-lg border bg-red-100 border-red-200 text-red-700 flex justify-between items-center'>
													<span className='text-xs font-bold'>Blocked ?</span>

													{(() => {
														const isSelfBlock = !selectedUser.isBlocked && currentUser && String(currentUser.phoneNumber) === String(selectedUser.phoneNumber);
														return (
															<button
																className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all text-slate-500 hover:text-slate-800 
                              ${isSelfBlock ? 'cursor-not-allowed opacity-40' : updatingUsers.has(`${selectedUser.id}:block`) ? 'cursor-not-allowed opacity-70' : 'hover:bg-white/40'}`}
																onClick={(e) => {
																	e.stopPropagation();
																	if (!isSelfBlock) toggleBlockStatus(selectedUser);
																}}
																disabled={!!isSelfBlock || isAnyUpdating(selectedUser.id)}
																title={isSelfBlock ? 'You cannot block your own account' : selectedUser.isBlocked ? 'Click to Unblock' : 'Click to Block'}
															>
																<RefreshCw
																	className={`w-3.5 h-3.5 ${updatingUsers.has(`${selectedUser.id}:block`) ? 'animate-spin' : ''}`}
																/>
															</button>
														);
													})()}

													<span className='text-xs font-black'>{selectedUser.isBlocked ? 'YES' : 'NO'}</span>
												</div>{' '}
												{/* Authority Status Box */}
												<div className='relative p-2.5 rounded-lg border bg-indigo-100 border-indigo-200 text-indigo-700 flex justify-between items-center'>
													<span className='text-xs font-bold'>Authority Rank</span>

													<button
														className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all text-slate-500 hover:text-slate-800 
                              ${updatingUsers.has(`${selectedUser.id}:authority`) ? 'cursor-not-allowed opacity-70' : 'hover:bg-white/40'}`}
														onClick={(e) => {
															e.stopPropagation();
															toggleAuthorityStatus(selectedUser);
														}}
														disabled={isAnyUpdating(selectedUser.id)}
														title={selectedUser.is_authority ? 'Demote Authority' : 'Promote to Authority'}
													>
														<RefreshCw
															className={`w-3.5 h-3.5 ${updatingUsers.has(`${selectedUser.id}:authority`) ? 'animate-spin' : ''}`}
														/>
													</button>

													<span className='text-xs font-black'>{selectedUser.is_authority ? 'AUTHORITY' : 'USER'}</span>
												</div>
												{selectedUser.is_authority && (
													<div className='relative px-2.5 py-[3px] rounded-lg border bg-amber-100 border-amber-200 text-amber-800 flex justify-between items-center'>
														<span className='text-xs font-bold'>Template</span>
														<div className='flex items-center gap-2'>
															{updatingUsers.has(`${selectedUser.id}:template`) && (
																<Loader2 className='w-3.5 h-3.5 animate-spin' />
															)}
															<TemplateSelector
																usr={selectedUser}
																isUpdating={isAnyUpdating(selectedUser.id)}
																updateTemplate={updateTemplate}
															/>
														</div>
													</div>
												)}
												{selectedUser.is_authority && (
													<div className='relative px-2.5 py-[3px] rounded-lg border bg-sky-100 border-sky-200 text-sky-800 flex justify-between items-center'>
														<span className='text-xs font-bold'>Organisation</span>
														<div className='flex items-center gap-2'>
															{updatingUsers.has(`${selectedUser.id}:organisation`) && (
																<Loader2 className='w-3.5 h-3.5 animate-spin' />
															)}
															<OrganisationSelector
																usr={selectedUser}
																isUpdating={isAnyUpdating(selectedUser.id)}
																updateOrganisation={updateOrganisation}
																organisations={organisations}
															/>
														</div>
													</div>
												)}
											</div>
										</div>

										{/* Location Details */}
										<div className='border border-gray-100 rounded-xl pt-4 px-4 pb-2 space-y-4'>
											<h3 className='text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2'>
												<Map className='w-3.5 h-3.5' /> Location Details
											</h3>
											<div className='space-y-2'>
												<div className='flex items-start gap-2'>
													<MapPin className='w-4 h-4 text-red-500 shrink-0 mt-0.5' />
													<div>
														<p className='text-sm font-bold text-gray-800'>
															{selectedUser.district || 'N/A'}, {selectedUser.state || 'N/A'}
														</p>
														<p className='text-[10px] font-medium text-gray-500 uppercase tracking-wide'>
															{selectedUser.country || 'India'}{' '}
															{selectedUser.pincode ? `• ${selectedUser.pincode}` : ''}
														</p>
													</div>
												</div>
											</div>
										</div>

										{/* System Timeline */}
										<div className='border border-gray-100 rounded-xl pt-4 px-4 pb-2 space-y-4'>
											<h3 className='text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2'>
												<Clock className='w-3.5 h-3.5' /> System Timeline
											</h3>
											<div className='flex flex-wrap gap-y-3'>
												<div className='w-1/2 text-left'>
													<p className='text-[9px] font-bold text-gray-400 uppercase leading-none mb-1'>Joined Date</p>
													<p className='text-xs font-bold text-gray-800'>{formatDate(selectedUser.createdAt)}</p>
												</div>
												<div className='w-1/2 text-left pl-2'>
													<p className='text-[9px] font-bold text-gray-400 uppercase leading-none mb-1'>Media Files</p>
													<p className='text-xs font-bold text-gray-800'>{selectedUser.mediaCount || 0}</p>
												</div>
											</div>
										</div>
									</div>
								</div>
							) : (
								<div className='flex flex-col gap-2 min-h-[300px]'>
									<div className='flex items-center justify-center py-3 border-b border-gray-100 relative'>
										<h3 className='m-0 p-0 text-sm font-black text-gray-400 uppercase tracking-widest leading-none'>
											{userPosts.length} posts
										</h3>
										{isLoadingPosts && <Loader2 className='w-3 h-3 animate-spin text-blue-600 absolute right-0' />}
									</div>

									{!isLoadingPosts && userPosts.length === 0 ? (
										<div className='flex flex-col items-center justify-center py-10 opacity-50'>
											<FileText className='w-10 h-10 text-gray-400 mb-2' />
											<p className='text-sm font-medium text-gray-500'>No posts found.</p>
										</div>
									) : (
										<div className='grid grid-cols-1 gap-3'>
											{userPosts.map((post) => {
												// Covered = anonymous and not yet revealed this visit. The card
												// shows only the date, can't be opened, and hides its actions.
												const isCovered = post.isAnonymous && !revealedPostIds.includes(post.id);
												return (
												<div
													key={post.id}
													className={`border border-gray-200 rounded-xl overflow-hidden transition-colors shadow-sm relative ${isCovered ? '' : 'hover:border-blue-300 cursor-pointer'}`}
													onClick={() => {
														if (isCovered) return;
														handlePostClick(post.id);
													}}
												>
													{/* Post Header */}
													<div className='p-3 bg-white flex justify-between items-center gap-3'>
														<div className='flex flex-col flex-1 text-left'>
															{!isCovered && (
																<h4
																	className='m-0 font-bold text-gray-900 text-sm leading-tight'
																	style={{
																		display: '-webkit-box',
																		WebkitLineClamp: 2,
																		WebkitBoxOrient: 'vertical',
																		overflow: 'hidden',
																	}}
																>
																	{post.title || 'Untitled Post'}
																</h4>
															)}
															{post.isAnonymous && (
																<span className='text-[8px] font-black text-blue-500 uppercase tracking-wider mt-0.5 bg-blue-50/50 w-fit px-1.5 py-0.5 rounded border border-blue-200'>
																	Anonymous Post
																</span>
															)}
														</div>
														<span className='text-[9px] font-bold text-gray-400 uppercase tracking-widest shrink-0 bg-gray-50 px-2 py-1 rounded-md'>
															{formatDate(post.createdAt)}
														</span>
													</div>

													{/* Covered placeholder — no title, no media, until revealed */}
													{isCovered && (
														<div className='bg-gray-100 h-64 w-full border-y border-gray-100 flex flex-col items-center justify-center gap-3'>
															<EyeOff className='w-10 h-10 text-gray-300' />
															<span className='text-2xl font-black text-gray-300 uppercase tracking-[0.2em]'>
																Anonymous Post
															</span>
															<span className='text-xs font-medium text-gray-400'>
																Type "showanonymous" below to view this post
															</span>
														</div>
													)}

													{/* Post Media Preview */}
													{!isCovered &&
														post.media &&
														post.media.length > 0 &&
														typeof post.media[0] === 'object' &&
														post.media[0] !== null && (
															<div className='bg-gray-100 h-64 w-full overflow-hidden border-y border-gray-100 flex items-center justify-center relative'>
																{post.media[0].link && extractYoutubeId(post.media[0].link) ? (
																	<YoutubeLinkPreview
																		videoId={extractYoutubeId(post.media[0].link)!}
																		postTitle={post.title}
																		videoLink={post.media[0].link}
																	/>
																) : (
																	<AdminPostMedia
																		src={mediaUrl(post.media[0].internalURL)}
																		alt='Post Media'
																		format={post.media[0].format}
																	/>
																)}
															</div>
														)}

													{/* Post Actions */}
													<div className='p-3 bg-gray-50/50 flex flex-col gap-2' onClick={(e) => e.stopPropagation()}>
														{isCovered ? (
															revealingPostId === post.id ? (
																<div className='animate-in fade-in zoom-in duration-200'>
																	<p className='text-[10px] font-black text-gray-400 tracking-widest mb-1.5 flex justify-center gap-1.5'>
																		<span>VERIFY REVEAL:</span>
																		<span className='text-blue-500 underline decoration-blue-200'>TYPE 'showanonymous'</span>
																	</p>
																	<div className='flex gap-2'>
																		<input
																			autoFocus
																			type='text'
																			value={confirmRevealInput}
																			onChange={(e) => setConfirmRevealInput(e.target.value)}
																			placeholder='showanonymous'
																			className='flex-1 bg-white border border-gray-200 px-3 py-1.5 rounded-lg text-xs focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all font-medium'
																		/>
																		<button
																			onClick={() => {
																				setRevealingPostId(null);
																				setConfirmRevealInput('');
																			}}
																			className='px-3 py-1.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors'
																		>
																			Cancel
																		</button>
																		<button
																			disabled={confirmRevealInput !== 'showanonymous'}
																			onClick={() => {
																				setRevealedPostIds((prev) =>
																					prev.includes(post.id) ? prev : [...prev, post.id]
																				);
																				setRevealingPostId(null);
																				setConfirmRevealInput('');
																			}}
																			className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${confirmRevealInput === 'showanonymous'
																					? 'bg-blue-600 text-white shadow shadow-blue-100 hover:bg-blue-700'
																					: 'bg-gray-100 text-gray-300 cursor-not-allowed border border-gray-200'
																				}`}
																		>
																			SHOW
																		</button>
																	</div>
																</div>
															) : (
																<button
																	onClick={() => {
																		setRevealingPostId(post.id);
																		setConfirmRevealInput('');
																	}}
																	className='w-full px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-100 text-blue-600 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1.5'
																>
																	<Eye className='w-3.5 h-3.5' /> Show Anonymous Post
																</button>
															)
														) : isDeletingPost === post.id ? (
															<div className='animate-in fade-in zoom-in duration-200'>
																<p className='text-[10px] font-black text-gray-400 tracking-widest mb-1.5 flex justify-center gap-1.5'>
																	<span>VERIFY DELETION:</span>
																	<span className='text-red-500 underline decoration-red-200'>TYPE 'deletepost'</span>
																</p>
																<div className='flex gap-2'>
																	<input
																		autoFocus
																		type='text'
																		value={confirmPostInput}
																		onChange={(e) => setConfirmPostInput(e.target.value)}
																		placeholder='deletepost'
																		className='flex-1 bg-white border border-gray-200 px-3 py-1.5 rounded-lg text-xs focus:border-red-500 focus:ring-2 focus:ring-red-50 outline-none transition-all font-medium'
																	/>
																	<button
																		onClick={() => {
																			setIsDeletingPost(null);
																			setConfirmPostInput('');
																		}}
																		className='px-3 py-1.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors'
																	>
																		Cancel
																	</button>
																	<button
																		disabled={confirmPostInput !== 'deletepost'}
																		onClick={() => handleDeletePost(post.id)}
																		className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${confirmPostInput === 'deletepost'
																				? 'bg-red-600 text-white shadow shadow-red-100 hover:bg-red-700'
																				: 'bg-gray-100 text-gray-300 cursor-not-allowed border border-gray-200'
																			}`}
																	>
																		DELETE
																	</button>
																</div>
															</div>
														) : (
															<div className='flex gap-2'>
																<button
																	className='flex-1 px-3 py-1.5 bg-white border border-gray-200 text-gray-400 rounded-lg text-xs font-bold shadow-sm cursor-not-allowed'
																	title='Not implemented'
																>
																	Block Post
																</button>
																<button
																	onClick={() => {
																		setIsDeletingPost(post.id);
																		setConfirmPostInput('');
																	}}
																	className='flex-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 border border-red-100 text-red-600 rounded-lg text-xs font-bold transition-colors'
																>
																	Delete Post
																</button>
															</div>
														)}
													</div>
												</div>
												);
											})}
										</div>
									)}
								</div>
							)}
						</div>

						{/* Modal Footer: Sticky Actions */}
						<div className='border-t border-gray-100 p-4 bg-gray-50/50 flex flex-col gap-3 shrink-0'>
							{!isDeleting ? (
								<div className='flex gap-3'>
									<button
										onClick={() => {
											if (modalView === 'info') {
												setModalView('posts');
												fetchUserPosts();
											} else {
												setModalView('info');
											}
										}}
										className='flex-1 bg-white border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-100 transition-colors shadow-sm'
									>
										{modalView === 'info' ? "View User's Posts" : 'View User Info'}
									</button>
									{isSelfAccount(selectedUser) ? (
										<button
											disabled
											title='You cannot delete your own admin account. Ask another admin to do it for you.'
											className='flex-1 bg-gray-100 text-gray-400 border border-gray-200 py-2.5 rounded-xl text-sm font-bold cursor-not-allowed'
										>
											Cannot Delete Your Own Account
										</button>
									) : (
										<button
											onClick={() => {
												setIsDeleting(true);
												setConfirmPhoneInput('');
											}}
											className='flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-bold hover:bg-red-700 transition-colors shadow-md shadow-red-100'
										>
											Permanently Delete User
										</button>
									)}
								</div>
							) : (
								<div className='animate-in fade-in slide-in-from-bottom-2 duration-200'>
									<p className='text-[10px] font-black text-gray-400 tracking-widest mb-2 flex justify-center gap-3'>
										<span>SAFETY VERIFICATION REQUIRED</span>
										<span className='text-red-600 underline decoration-red-200'>TYPE: deleteuser</span>
									</p>
									<div className='flex gap-2'>
										<input
											autoFocus
											type='text'
											value={confirmPhoneInput}
											onChange={(e) => setConfirmPhoneInput(e.target.value)}
											placeholder='type "deleteuser" to confirm'
											className='flex-1 bg-white border border-gray-200 px-4 py-2.5 rounded-xl text-sm focus:border-red-500 focus:ring-4 focus:ring-red-50 outline-none transition-all font-medium'
										/>
										<button
											onClick={() => {
												setIsDeleting(false);
												setConfirmPhoneInput('');
											}}
											className='px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm'
										>
											Cancel
										</button>
										<button
											disabled={confirmPhoneInput !== 'deleteuser'}
											onClick={handleDeleteUser}
											className={`px-5 py-2.5 rounded-xl text-sm font-black transition-all ${confirmPhoneInput === 'deleteuser'
													? 'bg-red-600 text-white shadow-lg shadow-red-100 hover:bg-red-700'
													: 'bg-gray-100 text-gray-300 cursor-not-allowed border border-gray-200'
												}`}
										>
											DELETE
										</button>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default UserAdminPanel;
